// client/src/coms/components/CreateChannelModal.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ComsModal from './ComsModal';

async function apiFetchJson(url, opts) {
  const res = await fetch(url, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts && opts.headers ? opts.headers : {}),
    },
  });

  let data = null;
  const ct = String(res.headers.get('content-type') || '');
  if (ct.includes('application/json')) {
    try {
      data = await res.json();
    } catch {}
  } else {
    try {
      const t = await res.text();
      data = t ? { text: t } : null;
    } catch {}
  }

  if (!res.ok) {
    const msg =
      (data && (data.error || data.detail || data.message)) ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function safeArr(v) {
  return Array.isArray(v) ? v : [];
}

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function normalizeRoleIds(value) {
  return Array.from(
    new Set(
      safeArr(value)
        .map((x) => toInt(x, 0))
        .filter((x) => x > 0)
    )
  );
}

function roleLabel(role) {
  const name = String((role && role.name) || '').trim();
  return name || 'Role';
}

function capitalise(str) {
  const s = String(str || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getMapLabel(map) {
  const name = String((map && map.name) || '').trim();
  const gameName = String((map && map.game_name) || '').trim();

  if (!name && !gameName) return `Map ${toInt(map && map.id, 0)}`;
  if (!name) return capitalise(gameName);
  if (!gameName) return name;
  if (name.toLowerCase() === gameName.toLowerCase()) return name;
  return `${name} — ${capitalise(gameName)}`;
}

function rowStyle(isCompact) {
  return {
    display: 'grid',
    gridTemplateColumns: isCompact ? '1fr' : '120px minmax(0, 1fr)',
    gap: isCompact ? 8 : 12,
    alignItems: 'start',
  };
}

function labelStyle(isCompact) {
  return {
    fontSize: 14,
    fontWeight: 700,
    color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.92 * var(--vx-ov, 1)))',
    paddingTop: isCompact ? 0 : 9,
  };
}

function fieldBoxStyle() {
  return {
    minWidth: 0,
  };
}

function inputStyle() {
  return {
    width: '100%',
    minHeight: 34,
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
    color: 'var(--vx-text, #fff)',
    padding: '0 12px',
    boxSizing: 'border-box',
    outline: 'none',
  };
}

function panelStyle() {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '10px 12px',
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    borderRadius: 10,
    background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))',
    width: '100%',
    boxSizing: 'border-box',
  };
}

function dropdownButtonStyle(open, disabled) {
  return {
    width: '100%',
    minHeight: 36,
    borderRadius: 10,
    border: open ? '1px solid rgba(var(--vx-link-rgb, 100,140,255), 0.65)' : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: disabled ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.02 * var(--vx-ov, 1)))' : 'var(--vxs-2, #0f1724)',
    color: 'var(--vx-text, #fff)',
    padding: '0 12px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    cursor: disabled ? 'default' : 'pointer',
    boxShadow: open ? '0 0 0 2px rgba(var(--vx-link-rgb, 100,140,255), 0.12)' : 'none',
  };
}

function dropdownMenuStyle() {
  return {
    marginTop: 8,
    borderRadius: 10,
    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
    background: 'var(--vxs-2, #0f1724)',
    overflow: 'hidden',
    boxShadow: '0 18px 36px rgba(0,0,0,0.35)',
  };
}

function dropdownItemStyle(selected) {
  return {
    width: '100%',
    minHeight: 36,
    padding: '0 12px',
    border: 0,
    background: selected ? 'rgba(110,168,255,0.18)' : 'transparent',
    color: 'var(--vx-text, #fff)',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  };
}

export default function CreateChannelModal({ server, user, onClose, onCreate }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('text');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [rolesBusy, setRolesBusy] = useState(false);
  const [rolesErr, setRolesErr] = useState('');
  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  const [mapsBusy, setMapsBusy] = useState(false);
  const [mapsErr, setMapsErr] = useState('');
  const [maps, setMaps] = useState([]);
  const [linkedMapId, setLinkedMapId] = useState('');
  const [mapMenuOpen, setMapMenuOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  const mapMenuRef = useRef(null);

  const canSubmit = String(name || '').trim().length >= 2;
  const serverId = server && server.id != null ? String(server.id) : '';
  const userId = user && user.id != null ? toInt(user.id, 0) : 0;
  const isFounder = userId === 1;

  useEffect(() => {
    function updateCompact() {
      if (typeof window === 'undefined') {
        setIsCompact(false);
        return;
      }
      setIsCompact(window.innerWidth <= 640);
    }

    updateCompact();
    window.addEventListener('resize', updateCompact);
    return () => window.removeEventListener('resize', updateCompact);
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadRoles() {
      if (!serverId) {
        if (alive) {
          setRoles([]);
          setSelectedRoleIds([]);
          setRolesBusy(false);
          setRolesErr('');
        }
        return;
      }

      setRolesBusy(true);
      setRolesErr('');

      try {
        const data = await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverId)}/roles`, {
          method: 'GET',
        });

        if (!alive) return;

        const nextRoles = safeArr(data && data.roles)
          .filter((role) => toInt(role && role.is_owner, 0) !== 1)
          .map((role) => ({
            ...role,
            id: toInt(role && role.id, 0),
          }))
          .filter((role) => role.id > 0);

        setRoles(nextRoles);
        setSelectedRoleIds(nextRoles.map((role) => role.id));
      } catch (e) {
        if (!alive) return;
        setRoles([]);
        setSelectedRoleIds([]);
        setRolesErr(e?.message || 'Failed to load roles');
      } finally {
        if (alive) setRolesBusy(false);
      }
    }

    loadRoles().catch(() => {});

    return () => {
      alive = false;
    };
  }, [serverId]);

  useEffect(() => {
    let alive = true;

    async function loadMaps() {
      if (!userId) {
        if (alive) {
          setMaps([]);
          setMapsBusy(false);
          setMapsErr('');
          setLinkedMapId('');
        }
        return;
      }

      setMapsBusy(true);
      setMapsErr('');

      try {
        const url = isFounder ? '/api/founder/maps' : `/api/user/id/${encodeURIComponent(userId)}/maps`;
        const data = await apiFetchJson(url, { method: 'GET' });

        if (!alive) return;

        const nextMaps = safeArr(data)
          .map((map) => ({
            ...map,
            id: toInt(map && map.id, 0),
            parent_map_id: map && map.parent_map_id != null ? toInt(map.parent_map_id, 0) : null,
          }))
          .filter((map) => map.id > 0)
          .filter((map) => !map.parent_map_id);

        setMaps(nextMaps);
      } catch (e) {
        if (!alive) return;
        setMaps([]);
        setMapsErr(e?.message || 'Failed to load maps');
      } finally {
        if (alive) setMapsBusy(false);
      }
    }

    loadMaps().catch(() => {});

    return () => {
      alive = false;
    };
  }, [userId, isFounder]);

  useEffect(() => {
    if (kind !== 'voice') {
      setLinkedMapId('');
      setMapMenuOpen(false);
    }
  }, [kind]);

  useEffect(() => {
    if (!mapMenuOpen) return;

    function onDown(e) {
      const el = mapMenuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setMapMenuOpen(false);
    }

    function onKey(e) {
      if (e && e.key === 'Escape') setMapMenuOpen(false);
    }

    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);

    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [mapMenuOpen]);

  const selectedRoleIdSet = useMemo(() => {
    return new Set(normalizeRoleIds(selectedRoleIds));
  }, [selectedRoleIds]);

  const selectedMap = useMemo(() => {
    const id = toInt(linkedMapId, 0);
    if (!id) return null;
    return safeArr(maps).find((map) => toInt(map && map.id, 0) === id) || null;
  }, [linkedMapId, maps]);

  function toggleRole(roleId) {
    const id = toInt(roleId, 0);
    if (!id) return;

    setSelectedRoleIds((prev) => {
      const list = normalizeRoleIds(prev);
      if (list.includes(id)) return list.filter((x) => x !== id);
      return [...list, id];
    });
  }

  function chooseLinkedMap(id) {
    const next = toInt(id, 0);
    setLinkedMapId(next > 0 ? String(next) : '');
    setMapMenuOpen(false);
  }

  async function submit(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!canSubmit || busy) return;

    setBusy(true);
    setErr('');

    try {
      if (!serverId) throw new Error('Missing server id');

      const cleanedRoleIds = normalizeRoleIds(selectedRoleIds);
      const hasRoles = cleanedRoleIds.length > 0;

      const body = {
        name: String(name || '').trim(),
        kind: String(kind || 'text').trim().toLowerCase(),
        visible_to_all: hasRoles ? 0 : 1,
        role_ids: hasRoles ? cleanedRoleIds : [],
      };

      if (body.kind === 'voice') {
        body.linked_map_id = linkedMapId ? toInt(linkedMapId, 0) || null : null;
      }

      const data = await apiFetchJson(`/api/coms/servers/${encodeURIComponent(serverId)}/channels`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (typeof onCreate === 'function') {
        try {
          await onCreate({
            serverId,
            channel: (data && data.channel) ? data.channel : null,
            raw: data,
          });
        } catch {}
      }

      onClose();
    } catch (e2) {
      setErr(e2?.message || 'Failed to create channel');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ComsModal
      title={`Create a channel in ${server?.name || 'server'}`}
      onClose={onClose}
      width={isCompact ? 'calc(100vw - 20px)' : '460px'}
      maxWidth="calc(100vw - 20px)"
      footer={
        <>
          <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="coms-mini-btn primary" onClick={submit} disabled={!canSubmit || busy}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <form
        onSubmit={submit}
        className="coms-form"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          width: '100%',
          minWidth: 0,
        }}
      >
        <div style={rowStyle(isCompact)}>
          <div style={labelStyle(isCompact)}>Type</div>

          <div style={fieldBoxStyle()}>
            <div
              className="coms-form-inline"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
                minHeight: 34,
              }}
            >
              <label className="coms-radio" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="radio"
                  name="kind"
                  value="text"
                  checked={kind === 'text'}
                  onChange={() => setKind('text')}
                />
                <span># Text</span>
              </label>

              <label className="coms-radio" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="radio"
                  name="kind"
                  value="voice"
                  checked={kind === 'voice'}
                  onChange={() => setKind('voice')}
                />
                <span>🔊 Voice</span>
              </label>
            </div>
          </div>
        </div>

        <div style={rowStyle(isCompact)}>
          <div style={labelStyle(isCompact)}>Name</div>

          <div style={fieldBoxStyle()}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === 'text' ? 'general' : 'Voice Lobby'}
              autoFocus
              style={inputStyle()}
            />
          </div>
        </div>

        {kind === 'voice' ? (
          <div style={rowStyle(isCompact)}>
            <div style={labelStyle(isCompact)}>Linked map</div>

            <div style={fieldBoxStyle()}>
              <div ref={mapMenuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    if (mapsBusy) return;
                    setMapMenuOpen((v) => !v);
                  }}
                  disabled={mapsBusy}
                  style={dropdownButtonStyle(mapMenuOpen, mapsBusy)}
                >
                  <span
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                    }}
                  >
                    {selectedMap ? getMapLabel(selectedMap) : 'No linked map'}
                  </span>
                  <span style={{ flexShrink: 0, opacity: 0.8 }}>{mapMenuOpen ? '▴' : '▾'}</span>
                </button>

                {mapMenuOpen ? (
                  <div style={dropdownMenuStyle()}>
                    <button
                      type="button"
                      onClick={() => chooseLinkedMap('')}
                      style={dropdownItemStyle(!linkedMapId)}
                    >
                      No linked map
                    </button>

                    {maps.map((map) => {
                      const selected = String(linkedMapId) === String(map.id);
                      return (
                        <button
                          key={map.id}
                          type="button"
                          onClick={() => chooseLinkedMap(map.id)}
                          style={dropdownItemStyle(selected)}
                        >
                          {getMapLabel(map)}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                Pick one of your maps to link this voice channel to.
              </div>

              {mapsBusy ? <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>Loading maps…</div> : null}

              {!mapsBusy && mapsErr ? (
                <div className="coms-form-error" style={{ marginTop: 8 }}>
                  {mapsErr}
                </div>
              ) : null}

              {!mapsBusy && !mapsErr && maps.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>No online maps found.</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div style={rowStyle(isCompact)}>
          <div style={labelStyle(isCompact)}>Visible to roles</div>

          <div style={fieldBoxStyle()}>
            <div
              style={{
                ...panelStyle(),
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Owner always has access and is not shown here.
              </div>

              {rolesBusy ? <div style={{ fontSize: 13, opacity: 0.8 }}>Loading roles…</div> : null}

              {!rolesBusy && rolesErr ? (
                <div className="coms-form-error" style={{ marginTop: 0 }}>
                  {rolesErr}
                </div>
              ) : null}

              {!rolesBusy && !rolesErr && roles.length === 0 ? (
                <div style={{ fontSize: 13, opacity: 0.8 }}>No assignable roles found.</div>
              ) : null}

              {!rolesBusy && !rolesErr && roles.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    width: '100%',
                  }}
                >
                  {roles.map((role) => {
                    const checked = selectedRoleIdSet.has(role.id);
                    return (
                      <label
                        key={role.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          width: '100%',
                          cursor: 'pointer',
                          padding: '2px 0',
                          boxSizing: 'border-box',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                          }}
                        >
                          {role.color ? (
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: String(role.color),
                                display: 'inline-block',
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                          <span
                            style={{
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {roleLabel(role)}
                          </span>
                        </span>

                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRole(role.id)}
                          style={{ flexShrink: 0 }}
                        />
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {err ? <div className="coms-form-error">{err}</div> : null}
      </form>
    </ComsModal>
  );
}